// src/lib/org_chart.ts — ORGANIGRAMA primăriei (brief 12.10, Lotul 3; caietele: „sistemul va permite
// definirea organigramei într-o manieră vizuală; fiecare departament va avea asociați utilizatori, alocați
// pe roluri"). PUR, partajat cu consola de platformă: din lista de membri + nomenclatorul de departamente
// (setările primăriei) compune coloanele (una per departament, potrivite case/diacritic-insensitiv prin
// `department_key`, plus „Fără departament") și pagina tipăribilă (HTML self-contained, A4 peisaj).
// Fără ierarhie între departamente — varianta minimă, doar afișare; tragerea cardurilor e în UI.

import { department_key } from "./entitlements"
import { escape_html } from "./print"

export interface OrgChartMember {
	user_id: string
	email: string
	display_name: string
	role: string
	department: string | null
}

export interface OrgChartColumn {
	/** Cheia pliată a departamentului (`department_key`); "" = fără departament. */
	key: string
	/** Forma afișată: cea din nomenclator, altfel prima întâlnită pe membri. */
	title: string
	members: OrgChartMember[]
}

export const NO_DEPARTMENT_TITLE = "Fără departament"

export const ROLE_BADGE: Record<string, string> = {
	owner: "Proprietar",
	admin: "Administrator",
	editor: "Editor",
	viewer: "Vizualizator",
}
const ROLE_ORDER: Record<string, number> = { owner: 0, admin: 1, editor: 2, viewer: 3 }

/** Numele de afișat al unui membru: numele setat, altfel partea din stânga a emailului. */
export function member_label(m: Pick<OrgChartMember, "display_name" | "email">): string {
	return m.display_name.trim() || m.email.split("@")[0]
}

/**
 * Coloanele organigramei: nomenclatorul de departamente (în ordinea lui) ∪ departamentele de pe membri
 * (în ordinea apariției), potrivite prin `department_key`; membrii sortați pe rol (owner → viewer), apoi
 * pe nume. Coloana „Fără departament" e MEREU ultima (e și ținta pentru „scoate din departament").
 */
export function build_org_chart(
	members: readonly OrgChartMember[],
	departments: readonly string[],
): OrgChartColumn[] {
	const columns: OrgChartColumn[] = []
	const by_key = new Map<string, OrgChartColumn>()
	const column_for = (title: string): OrgChartColumn => {
		const key = department_key(title)
		let col = by_key.get(key)
		if (!col) {
			col = { key, title, members: [] }
			by_key.set(key, col)
			columns.push(col)
		}
		return col
	}
	for (const d of departments) {
		const title = d.trim()
		if (title) {
			column_for(title)
		}
	}
	const none: OrgChartColumn = { key: "", title: NO_DEPARTMENT_TITLE, members: [] }
	for (const m of members) {
		const dep = (m.department ?? "").trim()
		;(dep ? column_for(dep) : none).members.push(m)
	}
	const sort = (list: OrgChartMember[]) =>
		list.sort(
			(a, b) =>
				(ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) ||
				member_label(a).localeCompare(member_label(b), "ro"),
		)
	for (const col of columns) {
		sort(col.members)
	}
	sort(none.members)
	columns.push(none)
	return columns
}

/**
 * Pagina tipăribilă a organigramei — HTML self-contained (fără scripturi, fără resurse externe), A4 peisaj;
 * textele sunt escapate (numele membrilor vin din conturi). Deschisă de client într-o fereastră nouă
 * (tiparul din lib/print: fereastra se deschide SINCRON, anti pop-up blocker).
 */
export function org_chart_html(
	org_name: string,
	columns: readonly OrgChartColumn[],
	date: string,
): string {
	const cols = columns
		.map(
			(c) => `
      <section class="col">
        <h2>${escape_html(c.title)} <span class="n">${c.members.length}</span></h2>
        ${
					c.members.length === 0
						? `<p class="gol">—</p>`
						: c.members
								.map(
									(m) => `
        <div class="card">
          <div class="nume">${escape_html(member_label(m))}</div>
          <div class="rol">${escape_html(ROLE_BADGE[m.role] ?? m.role)}</div>
          <div class="email">${escape_html(m.email)}</div>
        </div>`,
								)
								.join("")
				}
      </section>`,
		)
		.join("")
	return `<!doctype html>
<html lang="ro">
<head>
<meta charset="utf-8">
<title>Organigramă — ${escape_html(org_name)}</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  body { font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #0f172a; margin: 0; padding: 16px; }
  h1 { font-size: 20px; margin: 0 0 4px; }
  .sub { font-size: 12px; color: #475569; margin: 0 0 16px; }
  .org { text-align: center; margin-bottom: 14px; }
  .org .box { display: inline-block; border: 2px solid #0e7490; border-radius: 10px; padding: 8px 18px; font-weight: 700; }
  .cols { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-start; }
  .col { flex: 1 1 180px; min-width: 160px; border: 1px solid #cbd5e1; border-radius: 10px; padding: 8px; break-inside: avoid; }
  .col h2 { font-size: 13px; margin: 0 0 8px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; }
  .col h2 .n { float: right; font-weight: 400; color: #64748b; }
  .card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 6px 8px; margin-bottom: 6px; break-inside: avoid; }
  .nume { font-weight: 600; font-size: 12px; }
  .rol { font-size: 11px; color: #0e7490; }
  .email { font-size: 10px; color: #64748b; word-break: break-all; }
  .gol { color: #94a3b8; font-size: 12px; margin: 0; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
  <h1>Organigramă</h1>
  <p class="sub">${escape_html(org_name)} · generată ${escape_html(date)} din platforma Urbanism</p>
  <div class="org"><span class="box">${escape_html(org_name)}</span></div>
  <div class="cols">${cols}
  </div>
</body>
</html>`
}
